import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ExitToApp, Notifications, NotificationsOff, Devices, ArrowBack, Help } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { appStore } from '../stores/AppStore';
import { trackButtonClick, trackAuthEvent } from '../utils/analytics';

interface HeaderProps {
  onLogout: () => void;
  onRefreshDevices: () => void;
  onDevicesClick?: () => void;
  onHelpClick?: () => void;
}

const Header: React.FC<HeaderProps> = observer(({ onLogout, onRefreshDevices, onDevicesClick, onHelpClick }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [notificationPermission, setNotificationPermission] = React.useState<NotificationPermission>(
    appStore.getNotificationPermission()
  );

  const handleEnableNotifications = async () => {
    trackButtonClick('Enable Notifications', 'Header');
    const granted = await appStore.requestNotificationPermission();
    if (granted) {
      setNotificationPermission('granted');
      appStore.showToast('Browser notifications enabled!');
    } else {
      appStore.showToast('Notification permission denied');
    }
  };

  return (
    <AppBar position="static">
      <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          <img 
            src="/logo192.png" 
            alt="Bufferio App" 
            style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40, marginRight: isMobile ? 8 : 12 }}
          />
          <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" noWrap>
            {isMobile ? 'Bufferio' : 'Bufferio App'}
          </Typography>
        </Box>
        
        {/* Devices Button */}
        {onDevicesClick && (
          <IconButton
            color="inherit"
            onClick={() => {
              trackButtonClick('Devices', 'Header');
              onDevicesClick();
            }}
            size="small"
            title="My Devices"
            sx={{ mr: 1 }}
          >
            <Devices />
          </IconButton>
        )}

        {/* Notification Toggle */}
        {appStore.isNotificationSupported() && (
          <IconButton
            color="inherit"
            onClick={handleEnableNotifications}
            size="small"
            title={notificationPermission === 'granted' ? 'Notifications enabled' : 'Enable notifications'}
            sx={{ mr: 1 }}
          >
            {notificationPermission === 'granted' ? (
              <Notifications />
            ) : (
              <NotificationsOff />
            )}
          </IconButton>
        )}

        {/* Help Button */}
        {onHelpClick && (
          <IconButton
            color="inherit"
            onClick={() => {
              trackButtonClick('Help', 'Header');
              onHelpClick();
            }}
            size="small"
            title="Help & Information"
            sx={{ mr: 1 }}
          >
            <Help />
          </IconButton>
        )}
        
        {/* User Info - Avatar only on mobile */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            {user?.photoUrl ? (
              <Avatar
                src={user.photoUrl}
                alt={user?.username || user?.email}
                sx={{ width: 32, height: 32, mr: 1 }}
              />
            ) : (
              <Avatar
                alt={user?.username || user?.email}
                sx={{ 
                  width: 32, 
                  height: 32, 
                  mr: 1,
                  bgcolor: 'primary.main'
                }}
              >
                {(user?.username?.charAt(0) || user?.email?.charAt(0) || '?').toUpperCase()}
              </Avatar>
            )}
            <Typography variant="body2">
              {user?.email || user?.username}
            </Typography>
          </Box>
        )}
        
        {/* Mobile: Avatar only */}
        {isMobile && (
          <Box sx={{ mr: 1 }}>
            {user?.photoUrl ? (
              <Avatar
                src={user.photoUrl}
                alt={user?.username || user?.email}
                sx={{ width: 28, height: 28 }}
              />
            ) : (
              <Avatar
                alt={user?.username || user?.email}
                sx={{ 
                  width: 28, 
                  height: 28,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem'
                }}
              >
                {(user?.username?.charAt(0) || user?.email?.charAt(0) || '?').toUpperCase()}
              </Avatar>
            )}
          </Box>
        )}
        
        {/* Logout Button - Icon only on mobile */}
        {isMobile ? (
          <IconButton 
            color="inherit" 
            onClick={() => {
              trackButtonClick('Logout', 'Header');
              trackAuthEvent('logout', 'button');
              onLogout();
            }} 
            size="small"
          >
            <ExitToApp />
          </IconButton>
        ) : (
          <Button 
            color="inherit" 
            onClick={() => {
              trackButtonClick('Logout', 'Header');
              trackAuthEvent('logout', 'button');
              onLogout();
            }} 
            startIcon={<ExitToApp />}
          >
            Logout
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
});

export default Header;





